import React, { useState, useEffect } from 'react';
import { emailApi, type EmailEntry, type Domain } from '../utils/emailApi';

const ApiDebugCrud: React.FC = () => {
  const [apiBase, setApiBase] = useState<string>('');
  const [entries, setEntries] = useState<EmailEntry[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Form state
  const [newEntry, setNewEntry] = useState({
    email: '',
    url: '',
    domain_id: '',
    description: '',
    tags: ''
  });

  // Determine which API is being used
  useEffect(() => {
    const isDev = import.meta.env.DEV;
    const apiUrl = isDev 
      ? 'http://localhost:8787/api/email (LOCAL DEV)' 
      : 'https://drunk-email-api.b-9f2.workers.dev/api/email (PRODUCTION)';
    setApiBase(apiUrl);
  }, []);

  // Load data
  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [domainsData, entriesData] = await Promise.all([
        emailApi.getDomains(),
        emailApi.getEmailEntries({ limit: 10 })
      ]);
      setDomains(domainsData);
      setEntries(entriesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Create new entry
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEntry.email || !newEntry.url) return;

    setLoading(true);
    setError('');
    try {
      await emailApi.createEmailEntry({
        email: newEntry.email,
        url: newEntry.url,
        domain_id: newEntry.domain_id ? parseInt(newEntry.domain_id) : undefined,
        description: newEntry.description || undefined,
        tags: newEntry.tags ? newEntry.tags.split(',').map(t => t.trim()) : []
      });
      
      // Reset form
      setNewEntry({ email: '', url: '', domain_id: '', description: '', tags: '' });
      
      // Reload data
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create entry');
    } finally {
      setLoading(false);
    }
  };

  // Delete entry
  const handleDelete = async (id: number) => {
    if (!confirm('Delete this entry?')) return;
    
    setLoading(true);
    setError('');
    try {
      await emailApi.deleteEmailEntry(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setLoading(false);
    }
  };

  // Test API connectivity
  const testApi = async () => {
    setLoading(true);
    setError('');
    try {
      const timestamp = Date.now();
      const testEmail = `test-${timestamp}@example.dev`;
      
      // Create test entry
      const result = await emailApi.createEmailEntry({
        email: testEmail,
        url: 'https://test-connectivity.com',
        domain_id: domains.find(d => d.domain === 'example.dev')?.id,
        description: `API connectivity test - ${new Date().toLocaleString()}`,
        tags: ['test', 'connectivity']
      });
      
      console.log('Test entry created:', result);
      await loadData();
    } catch (err) {
      setError(`API Test Failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* API Info Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h2 className="text-xl font-bold text-blue-900 mb-2">🔧 API Debug & CRUD Test</h2>
        <div className="space-y-2 text-sm">
          <p><strong>Environment:</strong> {import.meta.env.DEV ? 'DEVELOPMENT' : 'PRODUCTION'}</p>
          <p><strong>API Endpoint:</strong> <code className="bg-blue-100 px-2 py-1 rounded">{apiBase}</code></p>
          <p><strong>User ID:</strong> {emailApi.getUserId()}</p>
        </div>
        
        <div className="mt-3 flex gap-2">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : '🔄 Refresh Data'}
          </button>
          <button
            onClick={testApi}
            disabled={loading}
            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
          >
            🧪 Test API
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="font-semibold text-red-900">❌ Error:</h3>
          <p className="text-red-700 text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Create Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">➕ Create New Email Entry</h3>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={newEntry.email}
                onChange={(e) => setNewEntry(prev => ({ ...prev, email: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="test@example.dev"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <input
                type="url"
                value={newEntry.url}
                onChange={(e) => setNewEntry(prev => ({ ...prev, url: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="https://example.com"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <select
                value={newEntry.domain_id}
                onChange={(e) => setNewEntry(prev => ({ ...prev, domain_id: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Select domain...</option>
                {domains.map(domain => (
                  <option key={domain.id} value={domain.id}>
                    {domain.domain} {domain.is_catch_all ? '(catch-all)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newEntry.description}
                onChange={(e) => setNewEntry(prev => ({ ...prev, description: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Optional description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={newEntry.tags}
                onChange={(e) => setNewEntry(prev => ({ ...prev, tags: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="tag1, tag2, tag3"
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading || !newEntry.email || !newEntry.url}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : '✨ Create Entry'}
          </button>
        </form>
      </div>

      {/* Data Display */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">📊 Current Data</h3>
        
        {/* Domains */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-2">Available Domains ({domains.length})</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {domains.map(domain => (
              <div key={domain.id} className="bg-gray-50 p-3 rounded text-sm">
                <div className="font-medium">{domain.domain}</div>
                <div className="text-gray-600">
                  {domain.is_catch_all ? '✅ Catch-all' : '❌ No catch-all'} • 
                  Default: {domain.default_email}
                </div>
                {domain.description && (
                  <div className="text-gray-500 text-xs mt-1">{domain.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Email Entries */}
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Recent Email Entries ({entries.length})</h4>
          <div className="space-y-2">
            {entries.length === 0 ? (
              <p className="text-gray-500 text-sm">No email entries found. Create one above to test!</p>
            ) : (
              entries.map(entry => (
                <div key={entry.id} className="bg-gray-50 p-3 rounded">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{entry.email}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        🔗 <a href={entry.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {entry.url}
                        </a>
                      </div>
                      {entry.description && (
                        <div className="text-xs text-gray-500 mt-1">{entry.description}</div>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-400 mt-2">
                        <span>ID: {entry.id}</span>
                        <span>Domain: {entry.domain_name || entry.domain_id}</span>
                        <span>Used: {entry.usage_count}x</span>
                        <span>Created: {new Date(entry.created_at).toLocaleDateString()}</span>
                      </div>
                      {entry.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {entry.tags.map((tag, i) => (
                            <span key={i} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="ml-2 px-2 py-1 bg-red-100 text-red-600 text-xs rounded hover:bg-red-200"
                      disabled={loading}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDebugCrud;